import {
  CalendarIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  FunnelIcon,
  PencilIcon,
  PlusIcon,
  RocketLaunchIcon,
  Squares2X2Icon,
  TrashIcon,
  UserIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import React, {
  DragEvent,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import {
  usePersistedPreference,
  useSessionFilter,
} from '../hooks/useFilterPersistence';
import { areasService, OrganizationArea } from '../services/areas';
import { companiesService, Company } from '../services/companies';
import { groupsService } from '../services/groups';
import { MasterPlanTask, masterPlanTaskService } from '../services/masterPlan';
import {
  KeyResult,
  keyResultApi,
  OrganizationOKR,
  planningConfigApi,
  PlanningCycle,
  planningCyclesApi,
} from '../services/planningCycles';
import { okrService } from '../services/okrs';
import { propositionService } from '../services/propositions';
import { segmentService } from '../services/segments';
import PlanningCycleSelector from './PlanningCycleSelector';
import TaskModal from './TaskModal';
import TaskDetailPanel from './TaskDetailPanel';
import { formatTaskStatus } from '../utils/enumFormatting';
import { API_URL } from '../constants/urls';

interface OrganizationMember {
  user_id: string;
  user_name: string | null;
  user_email: string;
  role: string;
  full_name?: string;
}

interface Position {
  id: string;
  segment_id: string;
  segment_name: string;
  proposition_id: string;
  proposition_name: string;
  focus_mode?: string;
  obvious_choice_score?: number;
  sam_value?: number;
  som_value?: number;
}

const UnifiedGravityPlan: React.FC = () => {
  const { selectedOrg, canEdit, isAdmin } = useOrganization();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Loading tasks...');
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [okrsLoaded, setOkrsLoaded] = useState(false);
  const [tasks, setTasks] = useState<MasterPlanTask[]>([]);
  const [organizationMembers, setOrganizationMembers] = useState<
    OrganizationMember[]
  >([]);
  const [areas, setAreas] = useState<OrganizationArea[]>([]);
  const [viewMode, setViewMode] = usePersistedPreference<'list' | 'kanban'>(
    'viewMode',
    'kanban',
    'unifiedGravityPlan',
  );
  const [isOKRPanelCollapsed, setIsOKRPanelCollapsed] = usePersistedPreference(
    'okrPanel.collapsed',
    false,
    'gravityPlan',
  );
  const [selectedOKR, setSelectedOKR] = useSessionFilter<string | null>(
    'selectedOKR',
    null,
    'unifiedGravityPlan',
  );
  const [selectedArea, setSelectedArea] = useSessionFilter<string>(
    'selectedArea',
    'all',
    'unifiedGravityPlan',
  );
  const [selectedOwner, setSelectedOwner] = useSessionFilter<string>(
    'selectedOwner',
    'all',
    'unifiedGravityPlan',
  );
  const [selectedOKROwner, setSelectedOKROwner] = useSessionFilter<string>(
    'selectedOKROwner',
    'all',
    'unifiedGravityPlan',
  );
  const [selectedContextType, setSelectedContextType] =
    useSessionFilter<string>(
      'selectedContextType',
      'all',
      'unifiedGravityPlan',
    );
  const [selectedContextObjectId, setSelectedContextObjectId] =
    useSessionFilter<string>(
      'selectedContextObjectId',
      'all',
      'unifiedGravityPlan',
    );
  const [taskScope, setTaskScope] = useState<'organization' | 'all'>('all');
  const [includeNestedTasks, setIncludeNestedTasks] = useState<boolean>(true);
  const [allOKRs, setAllOKRs] = useState<any[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);

  // OKR filter states
  const [okrCycleTypeFilter, setOkrCycleTypeFilter] = useSessionFilter<string>(
    'okrCycleTypeFilter',
    'all',
    'unifiedGravityPlan',
  );
  const [selectedSpecificCycle, setSelectedSpecificCycle] =
    useSessionFilter<string>(
      'selectedSpecificCycle',
      'all',
      'unifiedGravityPlan',
    );
  const [availableCycleTypes, setAvailableCycleTypes] = useState<string[]>([]);
  const [okrStatusFilter, setOkrStatusFilter] = useSessionFilter<string>(
    'okrStatusFilter',
    'active',
    'unifiedGravityPlan',
  );
  const [okrCorporateFilter, setOkrCorporateFilter] = useSessionFilter<string>(
    'okrCorporateFilter',
    'all',
    'unifiedGravityPlan',
  );

  // Validate and fix invalid stored filter values
  useEffect(() => {
    // Valid cycle type options
    const validCycleTypes = ['all', '3-year', 'year', 'quarter', '30-day'];
    if (!validCycleTypes.includes(okrCycleTypeFilter)) {
      setOkrCycleTypeFilter('all'); // Default to "all"
    }

    // Valid status options
    const validStatuses = ['active', 'draft', 'completed', 'cancelled'];
    if (!validStatuses.includes(okrStatusFilter)) {
      setOkrStatusFilter('active'); // Default to "active"
    }
  }, [
    okrCycleTypeFilter,
    okrStatusFilter,
    // Removed setter functions from dependencies as they don't change
  ]);

  // OKR Panel state
  const [allCycles, setAllCycles] = useState<PlanningCycle[]>([]);
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [currentCycle, setCurrentCycle] = useState<PlanningCycle | null>(null);
  const [configuredCycleTypes, setConfiguredCycleTypes] = useState<string[]>(
    [],
  );
  const [editingOKR, setEditingOKR] = useState<string | null>(null);
  const [editingOKRData, setEditingOKRData] = useState<any>(null);
  const [showAddOKR, setShowAddOKR] = useState(false);
  const [selectedCycleTypeForNewOKR, setSelectedCycleTypeForNewOKR] =
    useState<string>('30-day');
  const [newOKR, setNewOKR] = useState({
    objective: '',
    description: '',
    parent_okr_id: '',
    owner_id: '',
    status: 'draft' as const,
  });
  const [expandedOKRs, setExpandedOKRs] = useState<Record<string, boolean>>({});
  const [editingKeyResult, setEditingKeyResult] = useState<string | null>(null);
  const [showAddKeyResult, setShowAddKeyResult] = useState<string | null>(null);

  // Data for filters
  const [companies, setCompanies] = useState<Company[]>([]);
  const [segments, setSegments] = useState<any[]>([]);
  const [propositions, setPropositions] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);

  useEffect(() => {
    if (selectedOrg) {
      fetchData();
      // When organization context is selected, set the context object ID to the org ID
      if (selectedContextType === 'organization') {
        setSelectedContextObjectId(selectedOrg.id);
      }
    }
  }, [selectedOrg]);

  // Effect to set current cycle and initialize selection
  useEffect(() => {
    if (allCycles.length > 0 && !selectedCycleId) {
      // Find the current active cycle or default to first available
      const activeCycle = allCycles.find((c) => c.is_active);
      const defaultCycle = activeCycle || allCycles[0];
      if (defaultCycle) {
        setSelectedCycleId(defaultCycle.id);
        setCurrentCycle(defaultCycle);
      }
    }
  }, [allCycles, selectedCycleId]);

  // Effect to update current cycle when selection changes
  useEffect(() => {
    if (selectedCycleId && allCycles.length > 0) {
      const cycle = allCycles.find((c) => c.id === selectedCycleId);
      setCurrentCycle(cycle || null);
    }
  }, [selectedCycleId, allCycles]);

  // Effect to determine available cycle types from actual data
  useEffect(() => {
    if (allOKRs.length > 0 && allCycles.length > 0) {
      // Get unique cycle types from cycles that have OKRs
      const cycleTypesWithOKRs = new Set<string>();

      // Find which cycles have OKRs
      const cyclesWithOKRs = allCycles.filter((cycle) =>
        allOKRs.some((okr) => okr.planning_cycle_id === cycle.id),
      );

      // Collect the cycle types
      cyclesWithOKRs.forEach((cycle) => {
        cycleTypesWithOKRs.add(cycle.cycle_type);
      });

      // Order them properly: All, 3-yearly, Annual, Quarterly, Monthly
      const orderedTypes: string[] = ['all'];
      const typeOrder = ['3-year', 'year', 'quarter', '30-day'];

      typeOrder.forEach((type) => {
        if (cycleTypesWithOKRs.has(type)) {
          orderedTypes.push(type);
        }
      });

      setAvailableCycleTypes(orderedTypes);
    }
  }, [allOKRs, allCycles]);

  const fetchAllOKRs = async () => {
    if (!selectedOrg) return;

    try {
      // Fetch all OKRs for the organization
      const okrs = await okrService.listOKRs(selectedOrg.id, {
        include_children: true,
        include_relationships: true,
      });

      setAllOKRs(okrs);
    } catch (error) {
      console.error('Error fetching OKRs:', error);
      setAllOKRs([]);
    }
  };

  const fetchData = async () => {
    if (!selectedOrg) return;

    try {
      setLoading(true);
      setLoadingMessage('Loading tasks and organization data...');
      setLoadingProgress(0);
      const token = sessionStorage.getItem('authToken');

      // Phase 1: Load critical data first (tasks and basic org data)
      setLoadingProgress(1);
      const [tasksRes, areasRes, membersRes] = await Promise.all([
        masterPlanTaskService.list(selectedOrg.id, { include_org_tasks: true }),
        areasService.list(selectedOrg.id, false),
        axios.get(
          `${API_URL}/organizations/${selectedOrg.id}/members?organization_id=${selectedOrg.id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        ),
      ]);

      setTasks(tasksRes);
      setAreas(areasRes);
      setOrganizationMembers(
        membersRes.data.map((member: any) => ({
          ...member,
          full_name: member.user_name || member.user_email,
        })),
      );
      setTasksLoaded(true);
      setLoadingProgress(3);

      // Phase 2: Load OKRs and planning cycles
      setLoadingMessage('Loading OKRs and planning cycles...');
      const [cyclesRes, configRes] = await Promise.all([
        planningCyclesApi.listCycles(selectedOrg.id),
        planningConfigApi.getConfig(selectedOrg.id),
      ]);

      setAllCycles(cyclesRes);
      if (configRes) {
        setConfiguredCycleTypes(configRes.cycle_types || []);
        // Set available cycle types based on existing cycles
        const cycleTypes = new Set(
          cyclesRes.map((c: PlanningCycle) => c.cycle_type),
        );
        setAvailableCycleTypes(Array.from(cycleTypes));
      }
      setLoadingProgress(5);

      // Fetch OKRs
      await fetchAllOKRs();
      setOkrsLoaded(true);
      setLoadingProgress(7);

      // Phase 3: Load relationship data (can be done in background)
      setLoadingMessage('Loading context data...');
      const [companiesRes, segmentsRes, propsRes, groupsRes, matrixRes] =
        await Promise.all([
          companiesService.list(selectedOrg.id),
          segmentService.list(selectedOrg.id),
          propositionService.list(selectedOrg.id),
          groupsService.getGroups(selectedOrg.id),
          axios.get(
            `${API_URL}/positions/matrix?organization_id=${selectedOrg.id}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          ),
        ]);

      // Sort companies alphabetically by name
      setCompanies(companiesRes.sort((a, b) => a.name.localeCompare(b.name)));
      setSegments(segmentsRes);
      setPropositions(propsRes);
      setGroups(groupsRes);

      // Build positions from matrix data
      // New endpoint returns { propositions, segments, matrix }
      const matrixData = matrixRes.data.matrix || matrixRes.data;
      const positionsList = matrixData.map((pos: any) => ({
        id: pos.positioning_id,
        segment_id: pos.segment_id,
        segment_name: pos.segment_name,
        proposition_id: pos.proposition_id,
        proposition_name: pos.proposition_name,
        focus_mode: pos.focus_mode,
        obvious_choice_score: pos.obvious_choice_score,
        sam_value: pos.sam_value,
        som_value: pos.som_value,
      }));
      setPositions(positionsList);
      setLoadingProgress(10);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Memoized filtered tasks calculation
  const filteredTasks = useMemo(() => {
    let filtered = [...tasks];

    // Filter by area
    if (selectedArea !== 'all') {
      filtered = filtered.filter((task) => task.area_id === selectedArea);
    }

    // Filter by OKR
    if (selectedOKR) {
      filtered = filtered.filter((task) => task.okr_id === selectedOKR);
    }

    // Note: OKR Owner filtering is now handled in the OKR panel only
    // It filters which OKRs are displayed, not which tasks are shown
    // Task filtering by owner is handled by the Task Owner filter below

    // Filter by task owner
    if (selectedOwner !== 'all') {
      if (selectedOwner === 'unassigned') {
        filtered = filtered.filter((task) => !task.assigned_to_id);
      } else {
        filtered = filtered.filter(
          (task) => task.assigned_to_id === selectedOwner,
        );
      }
    }

    // Filter by context type and scope
    if (selectedContextType === 'all') {
      // Show all tasks when "All" is selected
      // Apply task scope filter
      if (taskScope === 'organization') {
        filtered = filtered.filter(
          (task) => task.context_type === 'organization' || !task.context_type,
        );
      }
      // taskScope === "all" shows everything
    } else if (selectedContextType === 'organization') {
      // Organization context - show only tasks that are organization-level
      // These are tasks that have context_type = "organization" or null/undefined
      filtered = filtered.filter(
        (task) => task.context_type === 'organization' || !task.context_type,
      );
    } else if (selectedContextObjectId !== 'all') {
      // Specific context object selected
      const effectiveContextObjectId = selectedContextObjectId;

      if (!includeNestedTasks) {
        // Simple filtering - only direct matches
        filtered = filtered.filter(
          (task) =>
            task.context_type === selectedContextType &&
            task.context_object_id === effectiveContextObjectId,
        );
      } else if (selectedContextType === 'group' && includeNestedTasks) {
        // For group filtering, include:
        // 1. Tasks directly linked to the group
        // 2. Tasks linked to segments in this group
        // 3. Tasks linked to propositions in this group
        // 4. Tasks linked to positions using segments/propositions in this group
        const group = groups.find((g) => g.id === selectedContextObjectId);
        if (group) {
          const segmentsInGroup = segments.filter(
            (s) => s.group_id === selectedContextObjectId,
          );
          const propositionsInGroup = propositions.filter(
            (p) => p.group_id === selectedContextObjectId,
          );
          const segmentIds = segmentsInGroup.map((s) => s.id);
          const propositionIds = propositionsInGroup.map((p) => p.id);

          // Find positions that use segments or propositions in this group
          const positionsInGroup = positions.filter(
            (pos) =>
              segmentIds.includes(pos.segment_id) ||
              propositionIds.includes(pos.proposition_id),
          );
          const positionIds = positionsInGroup.map((p) => p.id);

          filtered = filtered.filter((task) => {
            // Direct group link
            if (
              task.context_type === 'group' &&
              task.context_object_id === selectedContextObjectId
            )
              return true;
            // Segment link
            if (
              task.context_type === 'segment' &&
              task.context_object_id &&
              segmentIds.includes(task.context_object_id)
            )
              return true;
            // Proposition link
            if (
              task.context_type === 'proposition' &&
              task.context_object_id &&
              propositionIds.includes(task.context_object_id)
            )
              return true;
            // Position link
            if (
              task.context_type === 'position' &&
              task.context_object_id &&
              positionIds.includes(task.context_object_id)
            )
              return true;
            return false;
          });
        }
      } else if (selectedContextType === 'company') {
        // For company filtering
        if (selectedContextObjectId === 'all') {
          // Show all tasks with companies
          filtered = filtered.filter((task) => task.context_type === 'company');
        } else {
          // Filter by specific company
          filtered = filtered.filter(
            (task) =>
              task.context_type === 'company' &&
              task.context_object_id === selectedContextObjectId,
          );
        }
      } else if (selectedContextType === 'segment' && includeNestedTasks) {
        // For segment filtering, include:
        // 1. Tasks directly linked to the segment
        // 2. Tasks linked to positions using this segment
        const positionsWithSegment = positions.filter(
          (pos) => pos.segment_id === selectedContextObjectId,
        );
        const positionIds = positionsWithSegment.map((p) => p.id);

        filtered = filtered.filter((task) => {
          // Direct segment link
          if (
            task.context_type === 'segment' &&
            task.context_object_id === selectedContextObjectId
          )
            return true;
          // Position link
          if (
            task.context_type === 'position' &&
            task.context_object_id &&
            positionIds.includes(task.context_object_id)
          )
            return true;
          return false;
        });
      } else if (selectedContextType === 'proposition' && includeNestedTasks) {
        // For proposition filtering, include:
        // 1. Tasks directly linked to the proposition
        // 2. Tasks linked to positions using this proposition
        const positionsWithProposition = positions.filter(
          (pos) => pos.proposition_id === selectedContextObjectId,
        );
        const positionIds = positionsWithProposition.map((p) => p.id);

        filtered = filtered.filter((task) => {
          // Direct proposition link
          if (
            task.context_type === 'proposition' &&
            task.context_object_id === selectedContextObjectId
          )
            return true;
          // Position link
          if (
            task.context_type === 'position' &&
            task.context_object_id &&
            positionIds.includes(task.context_object_id)
          )
            return true;
          return false;
        });
      } else if (selectedContextType === 'organization') {
        if (!includeNestedTasks) {
          // For organization filtering without nesting, show only organization-level tasks
          filtered = filtered.filter(
            (task) =>
              task.context_type === 'organization' &&
              task.context_object_id === selectedOrg?.id,
          );
        }
        // If includeNestedTasks is true, show ALL tasks (no filtering needed)
      } else {
        // For other types (position), direct filtering
        filtered = filtered.filter(
          (task) =>
            task.context_type === selectedContextType &&
            task.context_object_id === selectedContextObjectId,
        );
      }
    }

    return filtered;
  }, [
    tasks,
    selectedArea,
    selectedOKR,
    selectedOwner,
    selectedContextType,
    selectedContextObjectId,
    taskScope,
    includeNestedTasks,
    allOKRs,
    groups,
    segments,
    propositions,
    positions,
    selectedOrg?.id,
  ]);

  const handleTaskUpdate = useCallback(
    async (taskId: string, updates: Partial<MasterPlanTask>) => {
      if (!selectedOrg) return;

      try {
        const updated = await masterPlanTaskService.update(
          selectedOrg.id,
          taskId,
          updates,
        );
        setTasks(tasks.map((t) => (t.id === taskId ? updated : t)));
      } catch (error) {
        // console.error removed
      }
    },
    [selectedOrg, tasks],
  );

  const handleTaskCreate = useCallback(
    async (task: MasterPlanTask) => {
      setTasks([...tasks, task]);
    },
    [tasks],
  );

  const handleTaskDelete = useCallback(
    async (taskId: string) => {
      if (
        !selectedOrg ||
        !window.confirm('Are you sure you want to delete this task?')
      )
        return;

      try {
        await masterPlanTaskService.delete(selectedOrg.id, taskId);
        setTasks(tasks.filter((t) => t.id !== taskId));
      } catch (error) {
        // console.error removed
      }
    },
    [selectedOrg, tasks],
  );

  // Helper function to get all related OKR IDs based on implicit time hierarchy
  const getAllRelatedOKRIds = (
    okrs: any[],
    targetCycleId: string,
  ): string[] => {
    // Find the target cycle
    const targetCycle = allCycles.find((c) => c.id === targetCycleId);
    if (!targetCycle) {
      return [];
    }

    const relatedIds = new Set<string>();
    const targetStart = new Date(targetCycle.start_date);
    const targetEnd = new Date(targetCycle.end_date);

    // Find all cycles that should be included based on implicit time hierarchy
    const relatedCycles = allCycles.filter((cycle) => {
      const cycleStart = new Date(cycle.start_date);
      const cycleEnd = new Date(cycle.end_date);

      // Include cycles based on the target cycle type
      if (targetCycle.cycle_type === '30-day') {
        // For 30-day: include parent quarter, year, and 3-year
        return (
          cycle.id === targetCycleId ||
          (['quarter', 'year', '3-year'].includes(cycle.cycle_type) &&
            cycleStart <= targetStart &&
            cycleEnd >= targetEnd)
        );
      } else if (targetCycle.cycle_type === 'quarter') {
        // For quarter: include parent year and 3-year, plus all 30-day cycles within
        return (
          cycle.id === targetCycleId ||
          (['year', '3-year'].includes(cycle.cycle_type) &&
            cycleStart <= targetStart &&
            cycleEnd >= targetEnd) ||
          (cycle.cycle_type === '30-day' &&
            cycleStart >= targetStart &&
            cycleEnd <= targetEnd)
        );
      } else if (targetCycle.cycle_type === 'year') {
        // For year: include parent 3-year, plus all quarters and 30-day cycles within
        return (
          cycle.id === targetCycleId ||
          (cycle.cycle_type === '3-year' &&
            cycleStart <= targetStart &&
            cycleEnd >= targetEnd) ||
          (['quarter', '30-day'].includes(cycle.cycle_type) &&
            cycleStart >= targetStart &&
            cycleEnd <= targetEnd)
        );
      } else if (targetCycle.cycle_type === '3-year') {
        // For 3-year: include all years, quarters, and 30-day cycles within
        return (
          cycle.id === targetCycleId ||
          (['year', 'quarter', '30-day'].includes(cycle.cycle_type) &&
            cycleStart >= targetStart &&
            cycleEnd <= targetEnd)
        );
      }
      return false;
    });

    // Get all OKRs from the related cycles
    okrs.forEach((okr) => {
      if (relatedCycles.some((cycle) => cycle.id === okr.planning_cycle_id)) {
        relatedIds.add(okr.id);
      }
    });

    return Array.from(relatedIds);
  };

  const getCycleTypeLabel = (type: string) => {
    switch (type) {
      case '30-day':
        return 'Monthly';
      case 'quarter':
        return 'Quarterly';
      case 'year':
        return 'Annual';
      case '3-year':
        return '3-Year';
      case 'all':
        return 'All';
      default:
        return type;
    }
  };

  // Get available specific cycles for selected cycle type
  const getAvailableSpecificCycles = useMemo(() => {
    if (okrCycleTypeFilter === 'all') {
      return [];
    }

    // Get cycles of the selected type that have OKRs
    const cyclesOfType = allCycles.filter((cycle) => {
      return (
        cycle.cycle_type === okrCycleTypeFilter &&
        allOKRs.some((okr) => okr.planning_cycle_id === cycle.id)
      );
    });

    // Sort by start date (most recent first)
    return cyclesOfType.sort(
      (a, b) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
    );
  }, [okrCycleTypeFilter, allCycles, allOKRs]);

  // Reset specific cycle selection when cycle type changes
  useEffect(() => {
    setSelectedSpecificCycle('all');
  }, [okrCycleTypeFilter, setSelectedSpecificCycle]);

  // Memoized OKR filtering function
  const getFilteredOKRsForDisplay = useMemo(() => {
    // Don't filter if OKRs haven't been loaded yet
    // This prevents the filter from running on empty initial data
    if (!okrsLoaded && allOKRs.length === 0) {
      return [];
    }

    let filtered = [...allOKRs];

    // Apply cycle type and specific cycle filtering
    if (okrCycleTypeFilter !== 'all') {
      // Filter by specific cycle type
      const cyclesOfType = allCycles.filter(
        (cycle) => cycle.cycle_type === okrCycleTypeFilter,
      );
      let cycleIds = cyclesOfType.map((cycle) => cycle.id);

      // If specific cycle selected, narrow down further
      if (selectedSpecificCycle !== 'all') {
        cycleIds = [selectedSpecificCycle];
      }

      filtered = filtered.filter((okr) =>
        cycleIds.includes(okr.planning_cycle_id),
      );
    }

    // Apply status filter
    filtered = filtered.filter((okr) => okr.status === okrStatusFilter);

    // Apply corporate filter
    if (okrCorporateFilter === 'corporate') {
      filtered = filtered.filter((okr) => okr.is_corporate);
    } else if (okrCorporateFilter === 'non-corporate') {
      filtered = filtered.filter((okr) => !okr.is_corporate);
    }

    // Apply owner filter
    if (selectedOKROwner !== 'all') {
      filtered = filtered.filter((okr) => okr.owner_id === selectedOKROwner);
    }

    return filtered;
  }, [
    allOKRs,
    allCycles,
    okrCycleTypeFilter,
    selectedSpecificCycle,
    okrStatusFilter,
    okrCorporateFilter,
    selectedOKROwner,
    okrsLoaded, // Added to ensure recalculation when OKRs are loaded
  ]);

  // Clear selected OKR if it's no longer visible after filter changes
  useEffect(() => {
    if (selectedOKR) {
      const isOKRVisible = getFilteredOKRsForDisplay.some(
        (okr) => okr.id === selectedOKR,
      );
      if (!isOKRVisible) {
        setSelectedOKR(null);
      }
    }
  }, [getFilteredOKRsForDisplay, selectedOKR, setSelectedOKR]);

  const getCycleForNewOKR = () => {
    // Find the cycle of the selected type, preferring the current cycle if it matches
    if (currentCycle?.cycle_type === selectedCycleTypeForNewOKR) {
      return currentCycle;
    }
    // Otherwise find any cycle of the selected type
    return allCycles.find((c) => c.cycle_type === selectedCycleTypeForNewOKR);
  };

  const handleCycleSelect = (cycleId: string | null) => {
    setSelectedCycleId(cycleId || '');
  };

  const handleCyclesUpdate = (cycles: PlanningCycle[]) => {
    setAllCycles(cycles);
    fetchAllOKRs();
  };

  // OKR Management functions
  const handleCreateOKR = async () => {
    const selectedCycle = getCycleForNewOKR();
    if (!selectedCycle || !newOKR.objective || !selectedOrg) return;

    try {
      // OKRs should not have relationships to business objects
      // Those belong on Tasks, not OKRs
      // Only parent-child OKR relationships are valid

      await okrService.createOKR(selectedOrg.id, {
        planning_cycle_id: selectedCycle.id,
        position_id: undefined, // OKRs don't directly link to positions
        objective: newOKR.objective,
        description: newOKR.description,
        parent_okr_id: newOKR.parent_okr_id || undefined,
        owner_id: newOKR.owner_id || undefined,
        status: newOKR.status,
        order_index: allOKRs.length,
        key_results: [],
        relationships: [], // No business object relationships
      });

      // Refresh OKRs
      await fetchAllOKRs();

      setShowAddOKR(false);
      setNewOKR({
        objective: '',
        description: '',
        parent_okr_id: '',
        owner_id: '',
        status: 'draft',
      });
    } catch (error) {
      // console.error removed
    }
  };

  const handleUpdateOKR = async (
    okrId: string,
    update: Partial<OrganizationOKR>,
  ) => {
    if (!selectedOrg) return;
    try {
      await okrService.updateOKR(okrId, selectedOrg.id, update);
      await fetchAllOKRs();
      setEditingOKR(null);
      setEditingOKRData(null);
    } catch (error) {
      // console.error removed
    }
  };

  // OKR deletion is handled in ObjectivesV2 and OKRDetail pages, not here

  // Key Result management functions
  const handleCreateKeyResult = async (
    okrId: string,
    keyResult: Partial<KeyResult>,
  ) => {
    if (!selectedOrg) return;
    try {
      await keyResultApi.createKeyResult(selectedOrg.id, okrId, {
        description: keyResult.description || '',
        target_value: keyResult.target_value || 100,
        current_value: keyResult.current_value || 0,
        unit: keyResult.unit || '%',
        status: keyResult.status || 'on-track',
        owner_id: keyResult.owner_id,
        order_index: 0,
      });
      await fetchAllOKRs();
      setShowAddKeyResult(null);
    } catch (error) {
      // console.error removed
    }
  };

  const handleUpdateKeyResult = async (
    krId: string,
    update: Partial<KeyResult>,
  ) => {
    if (!selectedOrg) return;

    // okr_id must be present in the update object
    if (!update.okr_id) {
      console.error('Cannot update key result: okr_id is missing');
      return;
    }

    try {
      // Map frontend status format to backend format
      const statusMap: Record<string, string> = {
        'on-track': 'on_track',
        'at-risk': 'at_risk',
        blocked: 'blocked',
        completed: 'completed',
      };

      const updateData: any = {
        description: update.description,
        target_value: update.target_value,
        current_value: update.current_value,
        unit: update.unit,
        owner_id: update.owner_id,
      };

      if (update.status) {
        updateData.status = statusMap[update.status] || update.status;
      }

      await okrService.updateKeyResult(
        update.okr_id,
        krId,
        selectedOrg.id,
        updateData,
      );
      await fetchAllOKRs();
      setEditingKeyResult(null);
    } catch (error) {
      console.error('Failed to update key result:', error);
    }
  };

  const handleDeleteKeyResult = async (okrId: string, krId: string) => {
    if (!window.confirm('Are you sure you want to delete this key result?'))
      return;
    if (!selectedOrg) return;

    try {
      await okrService.deleteKeyResult(okrId, krId, selectedOrg.id);
      await fetchAllOKRs();
    } catch (error) {
      // console.error removed
    }
  };

  const getStatusColor = (status: string, isOKR = true) => {
    if (isOKR) {
      const colors: Record<string, string> = {
        draft: 'bg-gray-100 text-gray-800',
        active: 'bg-blue-100 text-blue-800',
        completed: 'bg-green-100 text-green-800',
        cancelled: 'bg-red-100 text-red-800',
      };
      return colors[status] || 'bg-gray-100 text-gray-800';
    } else {
      const colors: Record<string, string> = {
        'on-track': 'bg-green-100 text-green-800',
        'at-risk': 'bg-yellow-100 text-yellow-800',
        blocked: 'bg-red-100 text-red-800',
        completed: 'bg-blue-100 text-blue-800',
      };
      return colors[status] || 'bg-gray-100 text-gray-800';
    }
  };

  // Calculate number of active OKR filters
  const getActiveOKRFilterCount = useMemo(() => {
    let count = 0;
    if (okrCycleTypeFilter !== 'all') count++;
    if (selectedSpecificCycle !== 'all') count++;
    if (okrStatusFilter !== 'active') count++;
    if (okrCorporateFilter !== 'all') count++;
    if (selectedOKROwner !== 'all') count++;
    return count;
  }, [
    okrCycleTypeFilter,
    selectedSpecificCycle,
    okrStatusFilter,
    okrCorporateFilter,
    selectedOKROwner,
  ]);

  // Helper function to get OKR relationship display
  const getOKRRelationshipDisplay = (okr: any) => {
    // OKRs no longer have direct relationships to business objects
    // Those relationships belong on Tasks, not OKRs
    // OKRs only have parent-child relationships and OKR-to-OKR/KR relationships

    // All OKRs are at the organization level
    return {
      type: 'Organization',
      name: selectedOrg?.name || 'Organization',
      icon: '🏢',
    };
  };

  // Render OKR with full editing capabilities
  const renderOKR = (okr: OrganizationOKR) => {
    const isExpanded = expandedOKRs[okr.id];
    const isSelected = selectedOKR === okr.id;
    const relationship = getOKRRelationshipDisplay(okr);

    // Determine edit permissions based on OKR type
    const canEditThis = okr.position_id ? canEdit : isAdmin;

    return (
      <div
        key={okr.id}
        className={`border rounded-lg p-4 cursor-pointer transition-all mb-2 ${
          isSelected
            ? 'border-blue-500 bg-blue-50 shadow-sm'
            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }`}
        onClick={(e) => {
          if (
            !(e.target as HTMLElement).closest('button') &&
            !(e.target as HTMLElement).closest('input') &&
            !(e.target as HTMLElement).closest('textarea') &&
            !(e.target as HTMLElement).closest('select')
          ) {
            setSelectedOKR(isSelected ? null : okr.id);
          }
        }}
      >
        <div className="space-y-3">
          <div className="flex items-start gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedOKRs({ ...expandedOKRs, [okr.id]: !isExpanded });
              }}
              className="p-1 hover:bg-gray-100 rounded flex-shrink-0 mt-0.5"
            >
              {isExpanded ? (
                <ChevronDownIcon className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRightIcon className="w-4 h-4 text-gray-500" />
              )}
            </button>

            <div className="flex-1 min-w-0">
              {editingOKR === okr.id ? (
                <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                  <input
                    value={editingOKRData?.objective || okr.objective}
                    onChange={(e) => {
                      setEditingOKRData({
                        ...editingOKRData,
                        objective: e.target.value,
                      });
                    }}
                    placeholder="Objective"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                  <textarea
                    value={editingOKRData?.description || okr.description || ''}
                    onChange={(e) => {
                      setEditingOKRData({
                        ...editingOKRData,
                        description: e.target.value,
                      });
                    }}
                    placeholder="Description (optional)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                    rows={2}
                  />
                  {/* Planning Cycle Selection */}
                  <PlanningCycleSelector
                    organizationId={selectedOrg?.id || ''}
                    currentCycleId={
                      editingOKRData?.planning_cycle_id || okr.planning_cycle_id
                    }
                    cycles={allCycles}
                    configuredCycleTypes={configuredCycleTypes}
                    onCycleSelect={(cycleId) => {
                      setEditingOKRData({
                        ...editingOKRData,
                        planning_cycle_id: cycleId,
                      });
                    }}
                    onCyclesUpdate={(updatedCycles) =>
                      setAllCycles(updatedCycles)
                    }
                  />

                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={
                        editingOKRData?.parent_okr_id || okr.parent_okr_id || ''
                      }
                      onChange={(e) => {
                        setEditingOKRData({
                          ...editingOKRData,
                          parent_okr_id: e.target.value,
                        });
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">No parent OKR</option>
                      {allOKRs
                        .filter((o) => o.id !== okr.id)
                        .map((parentOKR) => (
                          <option key={parentOKR.id} value={parentOKR.id}>
                            {parentOKR.objective}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Connected to:
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <select
                          value={(() => {
                            // Check temp relationship type first (during editing)
                            const okrData = allOKRs.find(
                              (o) => o.id === okr.id,
                            );
                            if ((okrData as any)?.temp_relationship_type) {
                              return (okrData as any).temp_relationship_type;
                            }
                            // Then check actual relationships
                            if (okr.position_id) return 'position';
                            // Check relationships from backend
                            if (
                              okr.relationships &&
                              okr.relationships.length > 0
                            ) {
                              return okr.relationships[0].relationship_type;
                            }
                            return 'organization';
                          })()}
                          onChange={(e) => {
                            const relType = e.target.value;
                            setAllOKRs(
                              allOKRs.map((o) => {
                                if (o.id === okr.id) {
                                  // Clear all relationship fields when switching types
                                  const updated = { ...o };

                                  // Clear position_id when switching away from position
                                  if (relType !== 'position') {
                                    updated.position_id = undefined;
                                  }

                                  // Set appropriate defaults for the new type
                                  if (
                                    relType === 'position' &&
                                    positions.length > 0
                                  ) {
                                    updated.position_id = positions[0].id;
                                  }

                                  // Store the relationship type in a temporary field
                                  // This will need backend support to persist
                                  (updated as any).temp_relationship_type =
                                    relType;

                                  return updated;
                                }
                                return o;
                              }),
                            );
                          }}
                          className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                        >
                          <option value="organization">Organization</option>
                          <option value="position">Position</option>
                          <option value="segment">Segment</option>
                          <option value="proposition">Proposition</option>
                          <option value="group">Group</option>
                        </select>

                        {/* Show appropriate selector based on relationship type */}
                        {(() => {
                          const okrData = allOKRs.find((o) => o.id === okr.id);
                          const relType =
                            (okrData as any)?.temp_relationship_type ||
                            (okr.position_id ? 'position' : 'organization');

                          switch (relType) {
                            case 'position':
                              return (
                                <select
                                  value={okr.position_id || ''}
                                  onChange={(e) => {
                                    setAllOKRs(
                                      allOKRs.map((o) =>
                                        o.id === okr.id
                                          ? {
                                              ...o,
                                              position_id: e.target.value,
                                            }
                                          : o,
                                      ),
                                    );
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                                >
                                  <option value="">Select position</option>
                                  {positions.map((position) => (
                                    <option
                                      key={position.id}
                                      value={position.id}
                                    >
                                      {position.proposition_name} ×{' '}
                                      {position.segment_name}
                                    </option>
                                  ))}
                                </select>
                              );
                            case 'segment':
                              return (
                                <select
                                  value={
                                    (okrData as any)?.temp_segment_id || ''
                                  }
                                  onChange={(e) => {
                                    setAllOKRs(
                                      allOKRs.map((o) =>
                                        o.id === okr.id
                                          ? ({
                                              ...o,
                                              temp_segment_id: e.target.value,
                                            } as any)
                                          : o,
                                      ),
                                    );
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                                >
                                  <option value="">Select segment</option>
                                  {segments.map((segment) => (
                                    <option key={segment.id} value={segment.id}>
                                      {segment.name}
                                    </option>
                                  ))}
                                </select>
                              );
                            case 'proposition':
                              return (
                                <select
                                  value={
                                    (okrData as any)?.temp_proposition_id || ''
                                  }
                                  onChange={(e) => {
                                    setAllOKRs(
                                      allOKRs.map((o) =>
                                        o.id === okr.id
                                          ? ({
                                              ...o,
                                              temp_proposition_id:
                                                e.target.value,
                                            } as any)
                                          : o,
                                      ),
                                    );
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                                >
                                  <option value="">Select proposition</option>
                                  {propositions.map((proposition) => (
                                    <option
                                      key={proposition.id}
                                      value={proposition.id}
                                    >
                                      {proposition.name}
                                    </option>
                                  ))}
                                </select>
                              );
                            case 'group':
                              return (
                                <select
                                  value={(okrData as any)?.temp_group_id || ''}
                                  onChange={(e) => {
                                    setAllOKRs(
                                      allOKRs.map((o) =>
                                        o.id === okr.id
                                          ? ({
                                              ...o,
                                              temp_group_id: e.target.value,
                                            } as any)
                                          : o,
                                      ),
                                    );
                                  }}
                                  className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                                >
                                  <option value="">Select group</option>
                                  {groups.map((group) => (
                                    <option key={group.id} value={group.id}>
                                      {group.name}{' '}
                                      {group.group_type
                                        ? `(${group.group_type})`
                                        : ''}
                                    </option>
                                  ))}
                                </select>
                              );
                            default:
                              return null;
                          }
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <select
                      value={editingOKRData?.owner_id || okr.owner_id || ''}
                      onChange={(e) => {
                        setEditingOKRData({
                          ...editingOKRData,
                          owner_id: e.target.value,
                        });
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="">No owner</option>
                      {organizationMembers.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.user_name || member.user_email}
                        </option>
                      ))}
                    </select>
                    <select
                      value={editingOKRData?.status || okr.status}
                      onChange={(e) => {
                        setEditingOKRData({
                          ...editingOKRData,
                          status: e.target.value,
                        });
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm"
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        if (editingOKRData || editingOKR) {
                          // Build update data from editing state
                          const updateData: any = {
                            objective:
                              editingOKRData?.objective || okr.objective,
                            description:
                              editingOKRData?.description || okr.description,
                            owner_id:
                              editingOKRData?.owner_id || okr.owner_id || null,
                            status: editingOKRData?.status || okr.status,
                          };

                          // Add optional fields
                          // Always include planning_cycle_id if it's changed
                          if (editingOKRData?.planning_cycle_id) {
                            updateData.planning_cycle_id =
                              editingOKRData.planning_cycle_id;
                          }
                          if (editingOKRData?.parent_okr_id !== undefined) {
                            updateData.parent_okr_id =
                              editingOKRData.parent_okr_id || null;
                          }

                          // For now, preserve existing relationships
                          // TODO: Add relationship editing to editingOKRData

                          handleUpdateOKR(okr.id, updateData);
                        }
                      }}
                      className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditingOKR(null);
                        setEditingOKRData(null);
                      }}
                      className="px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-sm text-gray-900 mb-1">
                        {okr.formatted_reference && (
                          <span className="text-xs font-mono text-gray-500 mr-2">
                            {okr.formatted_reference}:
                          </span>
                        )}
                        {okr.objective}
                        {okr.is_corporate && (
                          <span className="ml-2 text-xs text-blue-600">
                            [Corporate]
                          </span>
                        )}
                        <span className="ml-2 text-xs text-gray-500">
                          ({okr.status})
                        </span>
                      </h4>
                      {okr.description && (
                        <p className="text-xs text-gray-600 mb-2">
                          {okr.description}
                        </p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs">
                        <span
                          className={`px-2 py-1 rounded-full ${getStatusColor(okr.status)}`}
                        >
                          {okr.status}
                        </span>
                        {okr.is_corporate && (
                          <div className="flex items-center gap-1 text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                            <span>⭐</span>
                            <span className="font-medium">Corporate</span>
                          </div>
                        )}
                        {relationship.type !== 'Organization' && (
                          <div className="flex items-center gap-1 text-gray-600 bg-gray-100 px-2 py-1 rounded-full">
                            <span>{relationship.icon}</span>
                            <span className="font-medium">
                              {relationship.type}:
                            </span>
                            <span>{relationship.name}</span>
                          </div>
                        )}
                        {okr.planning_cycle && (
                          <div className="flex items-center gap-1 text-gray-500">
                            <CalendarIcon className="w-3 h-3" />
                            <span>{okr.planning_cycle.name}</span>
                          </div>
                        )}
                        {okr.owner_id && (
                          <div className="flex items-center gap-1 text-gray-500">
                            <UserIcon className="w-3 h-3" />
                            <span>
                              {organizationMembers.find(
                                (m) => m.user_id === okr.owner_id,
                              )?.user_name ||
                                organizationMembers.find(
                                  (m) => m.user_id === okr.owner_id,
                                )?.user_email}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Key Results */}
          {isExpanded && (
            <div className="ml-7 space-y-2">
              {okr.key_results && okr.key_results.length > 0 && (
                <div className="space-y-2">
                  {okr.key_results.map((kr) => (
                    <div
                      key={kr.id}
                      className="bg-white p-3 rounded-md border border-gray-100"
                    >
                      {editingKeyResult === kr.id ? (
                        <div
                          className="space-y-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            value={kr.description}
                            onChange={(e) => {
                              setAllOKRs(
                                allOKRs.map((o) =>
                                  o.id === okr.id
                                    ? {
                                        ...o,
                                        key_results: o.key_results.map(
                                          (k: KeyResult) =>
                                            k.id === kr.id
                                              ? {
                                                  ...k,
                                                  description: e.target.value,
                                                }
                                              : k,
                                        ),
                                      }
                                    : o,
                                ),
                              );
                            }}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="number"
                              value={kr.current_value}
                              onChange={(e) => {
                                setAllOKRs(
                                  allOKRs.map((o) =>
                                    o.id === okr.id
                                      ? {
                                          ...o,
                                          key_results: o.key_results.map(
                                            (k: KeyResult) =>
                                              k.id === kr.id
                                                ? {
                                                    ...k,
                                                    current_value:
                                                      parseFloat(
                                                        e.target.value,
                                                      ) || 0,
                                                  }
                                                : k,
                                          ),
                                        }
                                      : o,
                                  ),
                                );
                              }}
                              placeholder="Current"
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <input
                              type="number"
                              value={kr.target_value}
                              onChange={(e) => {
                                setAllOKRs(
                                  allOKRs.map((o) =>
                                    o.id === okr.id
                                      ? {
                                          ...o,
                                          key_results: o.key_results.map(
                                            (k: KeyResult) =>
                                              k.id === kr.id
                                                ? {
                                                    ...k,
                                                    target_value:
                                                      parseFloat(
                                                        e.target.value,
                                                      ) || 100,
                                                  }
                                                : k,
                                          ),
                                        }
                                      : o,
                                  ),
                                );
                              }}
                              placeholder="Target"
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                            <input
                              value={kr.unit}
                              onChange={(e) => {
                                setAllOKRs(
                                  allOKRs.map((o) =>
                                    o.id === okr.id
                                      ? {
                                          ...o,
                                          key_results: o.key_results.map(
                                            (k: KeyResult) =>
                                              k.id === kr.id
                                                ? { ...k, unit: e.target.value }
                                                : k,
                                          ),
                                        }
                                      : o,
                                  ),
                                );
                              }}
                              placeholder="Unit"
                              className="px-2 py-1 border border-gray-300 rounded text-sm"
                            />
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                const okrWithKR = allOKRs.find(
                                  (o) => o.id === okr.id,
                                );
                                const krToUpdate = okrWithKR?.key_results.find(
                                  (k: KeyResult) => k.id === kr.id,
                                );
                                if (krToUpdate) {
                                  handleUpdateKeyResult(kr.id, krToUpdate);
                                }
                              }}
                              className="p-1 text-green-600 hover:text-green-700"
                            >
                              <CheckIcon className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => {
                                setEditingKeyResult(null);
                                fetchData();
                              }}
                              className="p-1 text-gray-600 hover:text-gray-700"
                            >
                              <XMarkIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">
                              {kr.description}
                            </p>
                            <div className="mt-1 flex items-center gap-3">
                              <div className="flex items-center gap-1 text-xs text-gray-500">
                                <span>{kr.current_value}</span>
                                <span>/</span>
                                <span>{kr.target_value}</span>
                                <span>{kr.unit}</span>
                              </div>
                              {kr.target_value > 0 && (
                                <div className="flex-1 max-w-[100px]">
                                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                    <div
                                      className={`h-full ${
                                        kr.status === 'completed'
                                          ? 'bg-green-500'
                                          : kr.status === 'on-track'
                                            ? 'bg-blue-500'
                                            : kr.status === 'at-risk'
                                              ? 'bg-yellow-500'
                                              : 'bg-red-500'
                                      }`}
                                      style={{
                                        width: `${Math.min((kr.current_value / kr.target_value) * 100, 100)}%`,
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(kr.status, false)}`}
                              >
                                {kr.status}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!selectedOrg) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">No organization selected</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <RocketLaunchIcon className="w-6 h-6 text-blue-600" />
            <h1 className="text-lg font-semibold text-gray-900">
              Unified Gravity Plan
            </h1>
          </div>
          {/* View Toggle - Single Button Pattern */}
          <button
            onClick={() =>
              setViewMode(viewMode === 'kanban' ? 'list' : 'kanban')
            }
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            title={
              viewMode === 'kanban'
                ? 'Switch to List View'
                : 'Switch to Kanban View'
            }
          >
            {viewMode === 'kanban' ? (
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            ) : (
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Filter Bar - Separate Section */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex flex-wrap items-center gap-4">
          {/* Context Filters Group */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
            <Squares2X2Icon className="w-4 h-4 text-gray-500" />
            <label className="text-xs font-medium text-gray-600">
              Context:
            </label>
            <select
              value={selectedContextType}
              onChange={(e) => {
                const contextType = e.target.value;
                setSelectedContextType(contextType);
                // For organization context, automatically set the object ID
                if (contextType === 'organization' && selectedOrg) {
                  setSelectedContextObjectId(selectedOrg.id);
                } else {
                  setSelectedContextObjectId('all');
                }
              }}
              className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Tasks</option>
              <option value="organization">Organization</option>
              <option value="company">Company</option>
              <option value="group">Group</option>
              <option value="segment">Segment</option>
              <option value="proposition">Proposition</option>
              <option value="position">Position</option>
            </select>
            {/* Fixed width container for second dropdown to prevent layout shift */}
            <div className="min-w-[180px]">
              {selectedContextType !== 'organization' &&
              selectedContextType !== 'all' ? (
                <select
                  value={selectedContextObjectId}
                  onChange={(e) => setSelectedContextObjectId(e.target.value)}
                  className="w-full text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">
                    {selectedContextType === 'company'
                      ? 'All Companies'
                      : selectedContextType === 'group'
                        ? 'All Groups'
                        : selectedContextType === 'segment'
                          ? 'All Segments'
                          : selectedContextType === 'proposition'
                            ? 'All Propositions'
                            : selectedContextType === 'position'
                              ? 'All Positions'
                              : `All ${selectedContextType}s`}
                  </option>
                  {selectedContextType === 'company' &&
                    companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  {selectedContextType === 'group' &&
                    groups.map((group) => (
                      <option key={group.id} value={group.id}>
                        {group.name} ({group.group_type})
                      </option>
                    ))}
                  {selectedContextType === 'segment' &&
                    segments.map((segment) => (
                      <option key={segment.id} value={segment.id}>
                        {segment.name}
                      </option>
                    ))}
                  {selectedContextType === 'proposition' &&
                    propositions.map((proposition) => (
                      <option key={proposition.id} value={proposition.id}>
                        {proposition.name}
                      </option>
                    ))}
                  {selectedContextType === 'position' &&
                    positions.map((position) => (
                      <option key={position.id} value={position.id}>
                        {position.proposition_name} × {position.segment_name}
                      </option>
                    ))}
                </select>
              ) : (
                <div className="invisible">
                  <select
                    className="w-full text-sm border-gray-300 rounded-md"
                    disabled
                  >
                    <option>-</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Planning Filters Group */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
            <RocketLaunchIcon className="w-4 h-4 text-gray-500" />
            <label className="text-xs font-medium text-gray-600">
              Objectives:
            </label>
            <select
              value={selectedOKR || ''}
              onChange={(e) => setSelectedOKR(e.target.value || null)}
              className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All Objectives</option>
              {(() => {
                // Use the memoized filtered OKRs
                const filteredOKRs = getFilteredOKRsForDisplay;

                // Filtering is already done in getFilteredOKRsForDisplay

                const positionOKRs = filteredOKRs.filter(
                  (okr) => okr.position_id,
                );
                const companyOKRs = filteredOKRs.filter(
                  (okr) => !okr.position_id,
                );

                return (
                  <>
                    {positionOKRs.length > 0 && (
                      <optgroup label="Position OKRs">
                        {positionOKRs.map((okr) => (
                          <option key={okr.id} value={okr.id}>
                            {okr.objective}{' '}
                            {okr.planning_cycle &&
                              `(${okr.planning_cycle.name})`}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {companyOKRs.length > 0 && (
                      <optgroup label="Company OKRs">
                        {companyOKRs.map((okr) => (
                          <option key={okr.id} value={okr.id}>
                            {okr.objective}{' '}
                            {okr.planning_cycle &&
                              `(${okr.planning_cycle.name})`}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </>
                );
              })()}
            </select>
            <div className="w-px h-5 bg-gray-300" />
            <label className="text-xs font-medium text-gray-600">Area:</label>
            <select
              value={selectedArea}
              onChange={(e) => setSelectedArea(e.target.value)}
              className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Areas</option>
              {areas
                .filter((area) => area.is_active)
                .map((area) => (
                  <option key={area.id} value={area.id}>
                    {area.name}
                  </option>
                ))}
            </select>
          </div>

          {/* Assignment Filter */}
          <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-1.5">
            <UserIcon className="w-4 h-4 text-gray-500" />
            <label className="text-xs font-medium text-gray-600">
              Task Owner:
            </label>
            <select
              value={selectedOwner}
              onChange={(e) => setSelectedOwner(e.target.value)}
              className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              title="Filter by task owner"
            >
              <option value="all">All Owners</option>
              <option value="unassigned">Unassigned</option>
              {organizationMembers.map((member) => (
                <option key={member.user_id} value={member.user_id}>
                  {member.user_name || member.user_email}
                </option>
              ))}
            </select>
          </div>

          {/* My Tasks Quick Filter */}
          {user && (
            <button
              onClick={() => {
                if (selectedOwner === user.id) {
                  // If already showing my tasks, clear the filter
                  setSelectedOwner('all');
                } else {
                  // Show only my tasks
                  setSelectedOwner(user.id);
                }
              }}
              className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                selectedOwner === user.id
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
              title={
                selectedOwner === user.id
                  ? 'Showing only your tasks'
                  : 'Show only your tasks'
              }
            >
              <UserIcon className="w-4 h-4" />
              My Tasks
              {(() => {
                const myTaskCount = tasks.filter(
                  (task) => task.assigned_to_id === user.id,
                ).length;
                return myTaskCount > 0 ? (
                  <span
                    className={`px-1.5 py-0.5 text-xs rounded-full ${
                      selectedOwner === user.id
                        ? 'bg-blue-200 text-blue-800'
                        : 'bg-gray-200 text-gray-700'
                    }`}
                  >
                    {myTaskCount}
                  </span>
                ) : null;
              })()}
            </button>
          )}

          {/* Clear Filters Button */}
          {(selectedOKR ||
            selectedArea !== 'all' ||
            selectedOwner !== 'all' ||
            selectedContextType !== 'all' ||
            (selectedContextType !== 'all' &&
              selectedContextObjectId !== 'all')) && (
            <button
              onClick={() => {
                setSelectedOKR(null);
                setSelectedArea('all');
                setSelectedOwner('all');
                setSelectedContextType('all');
                setSelectedContextObjectId('all');
                setIncludeNestedTasks(true);
                setTaskScope('all');
              }}
              className="ml-auto flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-gray-200 rounded-md transition-colors"
            >
              <XMarkIcon className="w-4 h-4" />
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Left Sidebar - OKRs */}
        <div
          className={`${
            isOKRPanelCollapsed ? 'lg:w-[60px] w-[420px]' : 'w-[420px]'
          } bg-white border-r border-gray-200 overflow-y-auto transition-all duration-300 ease-in-out relative`}
        >
          {/* Collapsed View - only show on large screens when collapsed */}
          {isOKRPanelCollapsed && (
            <div className="hidden lg:flex lg:flex-col lg:items-center p-2">
              <button
                onClick={() => setIsOKRPanelCollapsed(false)}
                className="p-2 rounded-md hover:bg-gray-100 mb-4"
                title="Expand OKR panel"
              >
                <ChevronDoubleRightIcon className="h-5 w-5 text-gray-600" />
              </button>
              <div className="relative">
                <FunnelIcon
                  className="h-6 w-6 text-gray-600"
                  title={`${getActiveOKRFilterCount} active filter${getActiveOKRFilterCount !== 1 ? 's' : ''}`}
                />
                {getActiveOKRFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {getActiveOKRFilterCount}
                  </span>
                )}
              </div>
            </div>
          )}
          {/* Expanded view - show when not collapsed on desktop, always show on mobile */}
          <div className={`${isOKRPanelCollapsed ? 'lg:hidden' : 'block'} p-4`}>
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">
                  Objectives & Key Results
                </h2>
                {/* Collapse button - only on desktop */}
                <button
                  onClick={() => setIsOKRPanelCollapsed(true)}
                  className="hidden lg:flex p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  title="Collapse panel"
                >
                  <ChevronDoubleLeftIcon className="h-5 w-5" />
                </button>
              </div>

              {/* OKR Filters */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {/* Primary Filter: Cycle Type */}
                <select
                  value={okrCycleTypeFilter}
                  onChange={(e) => setOkrCycleTypeFilter(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All</option>
                  {availableCycleTypes
                    .filter((type) => type !== 'all')
                    .sort((a, b) => {
                      // Sort cycle types from longest to shortest
                      const order = ['3-year', 'year', 'quarter', '30-day'];
                      return order.indexOf(a) - order.indexOf(b);
                    })
                    .map((cycleType) => (
                      <option key={cycleType} value={cycleType}>
                        {getCycleTypeLabel(cycleType)}
                      </option>
                    ))}
                </select>

                {/* Secondary Filter: Specific Cycles (only when cycle type selected) */}
                {getAvailableSpecificCycles.length > 0 && (
                  <select
                    value={selectedSpecificCycle}
                    onChange={(e) => setSelectedSpecificCycle(e.target.value)}
                    className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">
                      All {getCycleTypeLabel(okrCycleTypeFilter)}
                    </option>
                    {getAvailableSpecificCycles.map((cycle) => (
                      <option key={cycle.id} value={cycle.id}>
                        {cycle.name}
                      </option>
                    ))}
                  </select>
                )}

                {/* Status Filter */}
                <select
                  value={okrStatusFilter}
                  onChange={(e) => setOkrStatusFilter(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </select>

                {/* Corporate Filter */}
                <select
                  value={okrCorporateFilter}
                  onChange={(e) => setOkrCorporateFilter(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Types</option>
                  <option value="corporate">Corporate</option>
                  <option value="non-corporate">Non-Corporate</option>
                </select>
                {/* Owner Filter */}
                <select
                  value={selectedOKROwner}
                  onChange={(e) => setSelectedOKROwner(e.target.value)}
                  className="text-sm border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  title="Filter by OKR owner"
                >
                  <option value="all">All Owners</option>
                  {organizationMembers.map((member) => (
                    <option key={member.user_id} value={member.user_id}>
                      {member.user_name || member.user_email}
                    </option>
                  ))}
                </select>
                {/* Reset OKR Filters Button */}
                {(okrCycleTypeFilter !== 'all' ||
                  selectedSpecificCycle !== 'all' ||
                  okrStatusFilter !== 'active' ||
                  okrCorporateFilter !== 'all' ||
                  selectedOKROwner !== 'all') && (
                  <button
                    onClick={() => {
                      setOkrCycleTypeFilter('all');
                      setSelectedSpecificCycle('all');
                      setOkrStatusFilter('active');
                      setOkrCorporateFilter('all');
                      setSelectedOKROwner('all');
                    }}
                    className="ml-auto flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 border border-gray-200 rounded-md transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Add OKR Form */}
            {showAddOKR && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium mb-3">Add New OKR</h3>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <select
                      value={selectedCycleTypeForNewOKR}
                      onChange={(e) =>
                        setSelectedCycleTypeForNewOKR(e.target.value)
                      }
                      className="w-full text-sm border-gray-300 rounded-md"
                    >
                      {configuredCycleTypes
                        .sort((a, b) => {
                          const order = ['30-day', 'quarter', 'year', '3-year'];
                          return order.indexOf(a) - order.indexOf(b);
                        })
                        .map((type) => {
                          const availableCycle = allCycles.find(
                            (c) => c.cycle_type === type,
                          );

                          return (
                            <option
                              key={type}
                              value={type}
                              disabled={!availableCycle}
                            >
                              {getCycleTypeLabel(type)} -{' '}
                              {availableCycle
                                ? availableCycle.name
                                : 'No cycle available'}
                            </option>
                          );
                        })}
                    </select>
                    {getCycleForNewOKR() && (
                      <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                        This OKR will be added to:{' '}
                        <strong>{getCycleForNewOKR()?.name}</strong>
                      </div>
                    )}
                  </div>

                  {/* OKRs should not have business object relationships - removed */}

                  <input
                    value={newOKR.objective}
                    onChange={(e) =>
                      setNewOKR({ ...newOKR, objective: e.target.value })
                    }
                    placeholder="Objective"
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <textarea
                    value={newOKR.description}
                    onChange={(e) =>
                      setNewOKR({ ...newOKR, description: e.target.value })
                    }
                    placeholder="Description (optional)"
                    className="w-full text-sm px-3 py-2 border border-gray-300 rounded-md"
                    rows={2}
                  />
                  <select
                    value={newOKR.parent_okr_id}
                    onChange={(e) =>
                      setNewOKR({ ...newOKR, parent_okr_id: e.target.value })
                    }
                    className="w-full text-sm border-gray-300 rounded-md"
                  >
                    <option value="">No parent OKR</option>
                    {allOKRs.map((okr) => (
                      <option key={okr.id} value={okr.id}>
                        {okr.objective}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={newOKR.owner_id}
                      onChange={(e) =>
                        setNewOKR({ ...newOKR, owner_id: e.target.value })
                      }
                      className="text-sm border-gray-300 rounded-md"
                    >
                      <option value="">No owner</option>
                      {organizationMembers.map((member) => (
                        <option key={member.user_id} value={member.user_id}>
                          {member.user_name || member.user_email}
                        </option>
                      ))}
                    </select>
                    <select
                      value={newOKR.status}
                      onChange={(e) =>
                        setNewOKR({ ...newOKR, status: e.target.value as any })
                      }
                      className="text-sm border-gray-300 rounded-md"
                    >
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateOKR}
                      disabled={!newOKR.objective || !getCycleForNewOKR()}
                      className="flex-1 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:bg-gray-300"
                    >
                      Create
                    </button>
                    <button
                      onClick={() => {
                        setShowAddOKR(false);
                        setNewOKR({
                          objective: '',
                          description: '',
                          parent_okr_id: '',
                          owner_id: '',
                          status: 'draft',
                        });
                      }}
                      className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-700 text-sm rounded-md hover:bg-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {(() => {
              // Filter OKRs based on current cycle and related hierarchies
              if (!currentCycle) {
                return (
                  <p className="text-gray-500 text-sm">
                    No planning cycle selected
                  </p>
                );
              }

              // Use the memoized filtered OKRs
              const filteredOKRs = getFilteredOKRsForDisplay;

              // Filtering is already done in getFilteredOKRsForDisplay

              if (filteredOKRs.length === 0) {
                return (
                  <p className="text-gray-500 text-sm">
                    No OKRs found for {currentCycle.name}
                  </p>
                );
              }

              // Group OKRs by cycle type for better organization
              const okrsByCycleType = filteredOKRs.reduce(
                (acc, okr) => {
                  const cycle = allCycles.find(
                    (c) => c.id === okr.planning_cycle_id,
                  );
                  if (cycle) {
                    if (!acc[cycle.cycle_type]) {
                      acc[cycle.cycle_type] = [];
                    }
                    acc[cycle.cycle_type].push(okr);
                  }
                  return acc;
                },
                {} as Record<string, any[]>,
              );

              const cycleTypeOrder = ['3-year', 'year', 'quarter', '30-day'];

              return (
                <div className="space-y-4">
                  {cycleTypeOrder
                    .filter((type) => okrsByCycleType[type]?.length > 0)
                    .map((cycleType) => (
                      <div key={cycleType} className="space-y-2">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {getCycleTypeLabel(cycleType)} OKRs
                        </div>
                        {okrsByCycleType[cycleType].map((okr: any) =>
                          renderOKR(okr),
                        )}
                      </div>
                    ))}
                </div>
              );
            })()}
          </div>
        </div>

        {/* Right Main Area - Tasks */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <UnifiedTaskManagement
            tasks={filteredTasks}
            viewMode={viewMode}
            canEdit={canEdit}
            organizationMembers={organizationMembers}
            allOKRs={allOKRs}
            organizationId={selectedOrg.id}
            positions={positions}
            segments={segments}
            propositions={propositions}
            groups={groups}
            onTaskUpdate={handleTaskUpdate}
            onTaskCreate={handleTaskCreate}
            onTaskDelete={handleTaskDelete}
            activeFilters={{
              okr_id: selectedOKR,
              area_id: selectedArea !== 'all' ? selectedArea : undefined,
              assigned_to_id:
                selectedOwner !== 'all' && selectedOwner !== 'unassigned'
                  ? selectedOwner
                  : undefined,
              context_type:
                selectedContextType !== 'all' ? selectedContextType : undefined,
              context_object_id:
                selectedContextObjectId !== 'all'
                  ? selectedContextObjectId
                  : undefined,
            }}
          />
        </div>
      </div>
    </div>
  );

  // UnifiedTaskManagement Component
  interface UnifiedTaskManagementProps {
    tasks: MasterPlanTask[];
    viewMode: 'list' | 'kanban';
    canEdit: boolean;
    organizationMembers: OrganizationMember[];
    allOKRs: any[];
    organizationId: string;
    positions: Position[];
    segments: any[];
    propositions: any[];
    groups: any[];
    onTaskUpdate: (taskId: string, updates: Partial<MasterPlanTask>) => void;
    onTaskCreate: (task: MasterPlanTask) => void;
    onTaskDelete: (taskId: string) => void;
    activeFilters?: {
      okr_id?: string | null;
      area_id?: string;
      assigned_to_id?: string;
      context_type?: string;
      context_object_id?: string;
    };
  }

  function UnifiedTaskManagement({
    tasks,
    viewMode,
    canEdit,
    organizationMembers,
    allOKRs,
    organizationId,
    positions,
    segments,
    propositions,
    groups,
    onTaskUpdate,
    onTaskCreate,
    onTaskDelete,
    activeFilters,
  }: UnifiedTaskManagementProps) {
    const [showAddTask, setShowAddTask] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [modalTask, setModalTask] = useState<MasterPlanTask | null>(null);
    // Task detail panel state
    const [showDetailPanel, setShowDetailPanel] = useState(false);
    const [selectedTask, setSelectedTask] = useState<MasterPlanTask | null>(
      null,
    );
    const [draggedTask, setDraggedTask] = useState<MasterPlanTask | null>(null);
    const [dragOverStatus, setDragOverStatus] = useState<string | null>(null);
    const initialBlockedVisibility =
      typeof window !== 'undefined' ? window.innerWidth >= 1440 : true;
    const [showBlockedPanel, setShowBlockedPanel] =
      usePersistedPreference<boolean>(
        'blockedPanelVisible',
        initialBlockedVisibility,
        'gravityPlan',
      );
    const blockedTasks = useMemo(
      () => tasks.filter((task) => task.status === 'blocked'),
      [tasks],
    );

    // Handle viewing a task (clicking on card)
    const handleViewTask = (task: MasterPlanTask) => {
      setSelectedTask(task);
      setShowDetailPanel(true);
      // Update URL with task ID
      setSearchParams({ taskId: task.id });
    };

    // Handle editing a task (clicking pencil icon)
    const handleEditTask = (task: MasterPlanTask, e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card click
      setSelectedTask(task);
      setShowDetailPanel(true);
      // Update URL with task ID
      setSearchParams({ taskId: task.id });
    };

    // Effect to handle URL parameter for task deep linking
    useEffect(() => {
      const taskIdParam = searchParams.get('taskId');

      if (taskIdParam && tasks.length > 0) {
        // Find the task with the matching ID
        const task = tasks.find((t) => t.id === taskIdParam);

        if (task && (!selectedTask || selectedTask.id !== task.id)) {
          // Open the task detail panel only if not already selected
          setSelectedTask(task);
          setShowDetailPanel(true);
        }
      } else if (!taskIdParam && showDetailPanel) {
        // URL doesn't have taskId but panel is open
        // This happens when user navigates back
        setSelectedTask(null);
        setShowDetailPanel(false);
      }
    }, [searchParams, tasks]);

    // Group tasks by Area
    const tasksByGroup = tasks.reduce(
      (acc, task) => {
        const groupKey = task.area_name || 'Unassigned';
        if (!acc[groupKey]) acc[groupKey] = [];
        acc[groupKey].push(task);
        return acc;
      },
      {} as Record<string, MasterPlanTask[]>,
    );

    const handleUpdateTask = async (
      taskId: string,
      updates: Partial<MasterPlanTask>,
    ) => {
      await onTaskUpdate(taskId, updates);
    };

    const handleDragStart = (
      e: DragEvent<HTMLDivElement>,
      task: MasterPlanTask,
    ) => {
      setDraggedTask(task);
      e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>, status: string) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      setDragOverStatus(status);
    };

    const handleDragLeave = () => {
      setDragOverStatus(null);
    };

    const handleDrop = async (
      e: DragEvent<HTMLDivElement>,
      newStatus: string,
    ) => {
      e.preventDefault();
      setDragOverStatus(null);

      if (draggedTask && formatTaskStatus(draggedTask.status) !== newStatus) {
        // Convert Title Case back to snake_case for API
        const statusMap: Record<string, string> = {
          'Not Started': 'not_started',
          'In Progress': 'in_progress',
          Complete: 'complete',
          Blocked: 'blocked',
        };
        const snakeCaseStatus = statusMap[newStatus] || 'not_started';
        await handleUpdateTask(draggedTask.id, {
          status: snakeCaseStatus as
            | 'not_started'
            | 'in_progress'
            | 'complete'
            | 'blocked',
        });
      }
      setDraggedTask(null);
    };

    const getPositionName = (positionId: string) => {
      const position = positions.find((p) => p.id === positionId);
      return position
        ? `${position.proposition_name} × ${position.segment_name}`
        : '';
    };

    const renderKanbanView = () => (
      <div className="flex flex-col h-full">
        {/* Fixed Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
          <h2 className="text-lg font-semibold">Tasks - Kanban View</h2>
          {canEdit && (
            <button
              onClick={() => {
                setShowAddTask(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4" />
              Add Task
            </button>
          )}
        </div>

        {/* Scrollable columns area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {['Not Started', 'In Progress', 'Complete'].map((status) => (
              <div
                key={status}
                className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 min-w-[320px] transition-all ${
                  dragOverStatus === status
                    ? 'ring-2 ring-blue-400 border-blue-400 shadow-lg'
                    : ''
                }`}
                onDragOver={(e) => handleDragOver(e, status)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, status)}
              >
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                  <h3 className="font-semibold text-gray-900 text-base">
                    {status}
                  </h3>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      status === 'Complete'
                        ? 'bg-green-100 text-green-800'
                        : status === 'In Progress'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {(() => {
                      const count = tasks.filter(
                        (task) => formatTaskStatus(task.status) === status,
                      ).length;
                      return count;
                    })()}
                  </span>
                </div>
                <div className="space-y-3 min-h-[300px] p-1">
                  {tasks
                    .filter((task) => formatTaskStatus(task.status) === status)
                    .map((task) => (
                      <div
                        key={task.id}
                        draggable={canEdit}
                        onDragStart={(e) => handleDragStart(e, task)}
                        onClick={() => handleViewTask(task)}
                        className={`group bg-white rounded-lg p-4 border border-gray-200 hover:shadow-lg hover:border-blue-400 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer ${
                          draggedTask?.id === task.id ? 'opacity-50' : ''
                        }`}
                      >
                        {/* Header with title and actions */}
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="flex-1">
                            {task.formatted_reference && (
                              <span className="text-xs font-mono text-gray-500 block mb-1">
                                {task.formatted_reference}
                              </span>
                            )}
                            <h4 className="font-semibold text-sm text-gray-900 leading-tight">
                              {task.title}
                            </h4>
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => handleEditTask(task, e)}
                                className="p-1 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                              >
                                <PencilIcon className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (task.id) onTaskDelete(task.id);
                                }}
                                className="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                              >
                                <TrashIcon className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Quick status change - more compact for kanban */}
                        {canEdit && (
                          <div className="mb-3">
                            <select
                              value={task.status}
                              onChange={(e) =>
                                handleUpdateTask(task.id, {
                                  status: e.target.value as any,
                                })
                              }
                              className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-gray-50 hover:bg-white focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In Progress</option>
                              <option value="complete">Complete</option>
                              <option value="blocked">Blocked</option>
                            </select>
                          </div>
                        )}

                        {/* Priority and Area badges */}
                        <div className="flex flex-wrap items-center gap-1.5 mb-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              task.priority === 'high'
                                ? 'bg-red-100 text-red-800'
                                : task.priority === 'medium'
                                  ? 'bg-yellow-100 text-yellow-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {task.priority
                              ? task.priority.charAt(0).toUpperCase() +
                                task.priority.slice(1)
                              : ''}
                          </span>
                          {task.area_name && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500 text-white">
                              {task.area_name}
                            </span>
                          )}
                        </div>

                        {/* Context information */}
                        {task.context_type &&
                          task.context_object_id &&
                          task.context_type !== 'organization' && (
                            <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                              <Squares2X2Icon className="w-3 h-3 text-gray-400" />
                              <span className="truncate">
                                {task.context_type === 'position' &&
                                  getPositionName(task.context_object_id)}
                                {task.context_type === 'segment' &&
                                  segments.find(
                                    (s) => s.id === task.context_object_id,
                                  )?.name}
                                {task.context_type === 'proposition' &&
                                  propositions.find(
                                    (p) => p.id === task.context_object_id,
                                  )?.name}
                                {task.context_type === 'group' &&
                                  groups.find(
                                    (g) => g.id === task.context_object_id,
                                  )?.name}
                                {task.context_type === 'company' &&
                                  companies.find(
                                    (c) => c.id === task.context_object_id,
                                  )?.name}
                              </span>
                            </div>
                          )}

                        {/* OKR alignment */}
                        {task.okr_id && (
                          <div className="flex items-center gap-1 text-xs text-gray-600 mb-3">
                            <RocketLaunchIcon className="w-3 h-3 text-gray-400" />
                            <span className="truncate">
                              {(() => {
                                const okr = allOKRs.find(
                                  (o) => o.id === task.okr_id,
                                );
                                if (!okr) return 'Unknown OKR';
                                return okr.formatted_reference
                                  ? `${okr.formatted_reference}: ${okr.objective}`
                                  : okr.objective;
                              })()}
                            </span>
                          </div>
                        )}

                        {/* Assignee and due date */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <div className="flex items-center gap-1 text-xs text-gray-500">
                            {task.assigned_to_id ? (
                              <>
                                <UserIcon className="w-3 h-3" />
                                <span className="truncate max-w-[100px]">
                                  {organizationMembers.find(
                                    (m) => m.user_id === task.assigned_to_id,
                                  )?.user_name ||
                                    organizationMembers.find(
                                      (m) => m.user_id === task.assigned_to_id,
                                    )?.user_email}
                                </span>
                              </>
                            ) : (
                              <span className="text-gray-400">Unassigned</span>
                            )}
                          </div>
                          {task.due_date && (
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <CalendarIcon className="w-3 h-3" />
                              <span>
                                {new Date(task.due_date).toLocaleDateString(
                                  'en-US',
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                  },
                                )}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Fixed Blocked Tasks Section at bottom */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-2">
              <svg
                className="h-5 w-5 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <h3 className="text-sm font-semibold text-gray-900 sm:text-base">
                Blocked Tasks
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
                {blockedTasks.length}
              </span>
              <button
                onClick={() => setShowBlockedPanel(!showBlockedPanel)}
                className="rounded-md border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:bg-white hover:text-gray-900 sm:text-sm"
              >
                {showBlockedPanel ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          {showBlockedPanel && (
            <div
              className={`mt-2 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 p-3 transition-all ${
                dragOverStatus === 'Blocked' ? 'border-red-300 bg-red-50' : ''
              }`}
              onDragOver={(e) => handleDragOver(e, 'Blocked')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'Blocked')}
            >
              {blockedTasks.length === 0 ? (
                <div className="flex h-[140px] items-center justify-center">
                  <p className="text-sm text-gray-500">No blocked tasks</p>
                </div>
              ) : (
                <div className="h-[160px] overflow-y-auto">
                  <div className="grid grid-cols-1 gap-3 p-1 sm:grid-cols-2 xl:grid-cols-3">
                    {blockedTasks.map((task) => (
                      <div
                        key={task.id}
                        draggable={canEdit}
                        onDragStart={(e) => handleDragStart(e, task)}
                        className={`group rounded-lg border-2 border-red-200 bg-white p-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-red-400 hover:shadow-lg sm:p-3.5 ${
                          canEdit ? 'cursor-move' : ''
                        } ${draggedTask?.id === task.id ? 'opacity-50' : ''}`}
                      >
                        <div className="mb-2 flex items-start justify-between gap-2">
                          <div className="flex-1">
                            {task.formatted_reference && (
                              <span className="mb-1 block font-mono text-[10px] text-gray-500 sm:text-xs">
                                {task.formatted_reference}
                              </span>
                            )}
                            <h4 className="text-sm font-semibold leading-tight text-red-900">
                              {task.title}
                            </h4>
                          </div>
                          {canEdit && (
                            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                              <button
                                onClick={(e) => handleEditTask(task, e)}
                                className="rounded p-1 text-gray-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                              >
                                <PencilIcon className="h-3.5 w-3.5" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (task.id) onTaskDelete(task.id);
                                }}
                                className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                              >
                                <TrashIcon className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          )}
                        </div>

                        {canEdit && (
                          <div className="mb-2">
                            <select
                              value={task.status}
                              onChange={(e) =>
                                handleUpdateTask(task.id, {
                                  status: e.target.value as any,
                                })
                              }
                              className="cursor-pointer rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs transition-all hover:bg-white focus:border-transparent focus:bg-white focus:ring-2 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="not_started">Not Started</option>
                              <option value="in_progress">In Progress</option>
                              <option value="complete">Complete</option>
                              <option value="blocked">Blocked</option>
                            </select>
                          </div>
                        )}

                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600 sm:text-xs">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium sm:text-xs ${
                                task.priority === 'high'
                                  ? 'bg-red-100 text-red-800'
                                  : task.priority === 'medium'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {task.priority
                                ? task.priority.charAt(0).toUpperCase() +
                                  task.priority.slice(1)
                                : ''}
                            </span>
                            {task.area_name && (
                              <span className="inline-flex items-center rounded-full bg-gray-500 px-2 py-0.5 text-[11px] font-medium text-white sm:text-xs">
                                {task.area_name}
                              </span>
                            )}
                          </div>
                          {task.due_date && (
                            <div className="flex items-center gap-1 text-gray-500">
                              <CalendarIcon className="h-3 w-3" />
                              <span>
                                {new Date(task.due_date).toLocaleDateString(
                                  'en-US',
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                  },
                                )}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="mb-2 flex flex-wrap items-center gap-2 text-[11px] text-gray-600 sm:text-xs">
                          {task.context_type &&
                            task.context_object_id &&
                            task.context_type !== 'organization' && (
                              <div className="flex items-center gap-1">
                                <Squares2X2Icon className="h-3 w-3 text-gray-400" />
                                <span className="truncate">
                                  {task.context_type === 'position' &&
                                    getPositionName(task.context_object_id)}
                                  {task.context_type === 'segment' &&
                                    segments.find(
                                      (s) => s.id === task.context_object_id,
                                    )?.name}
                                  {task.context_type === 'proposition' &&
                                    propositions.find(
                                      (p) => p.id === task.context_object_id,
                                    )?.name}
                                  {task.context_type === 'group' &&
                                    groups.find(
                                      (g) => g.id === task.context_object_id,
                                    )?.name}
                                  {task.context_type === 'company' &&
                                    companies.find(
                                      (c) => c.id === task.context_object_id,
                                    )?.name}
                                </span>
                              </div>
                            )}
                          {task.okr_id && (
                            <div className="flex items-center gap-1">
                              <RocketLaunchIcon className="h-3 w-3 text-gray-400" />
                              <span className="truncate">
                                {(() => {
                                  const okr = allOKRs.find(
                                    (o) => o.id === task.okr_id,
                                  );
                                  if (!okr) return 'Unknown OKR';
                                  return okr.formatted_reference
                                    ? `${okr.formatted_reference}: ${okr.objective}`
                                    : okr.objective;
                                })()}
                              </span>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-1 text-[11px] text-gray-500 sm:text-xs">
                          {task.assigned_to_id ? (
                            <>
                              <UserIcon className="h-3 w-3" />
                              <span className="max-w-[120px] truncate">
                                {organizationMembers.find(
                                  (m) => m.user_id === task.assigned_to_id,
                                )?.user_name ||
                                  organizationMembers.find(
                                    (m) => m.user_id === task.assigned_to_id,
                                  )?.user_email}
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-400">Unassigned</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );

    const renderListView = () => (
      <div className="flex flex-col h-full">
        {/* Fixed Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200">
          <h2 className="text-lg font-semibold">Tasks - List View</h2>
          {canEdit && (
            <button
              onClick={() => {
                setShowAddTask(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4" />
              Add Task
            </button>
          )}
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-6">
            {Object.entries(tasksByGroup).map(([groupKey, groupTasks]) => (
              <div
                key={groupKey}
                className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="font-semibold text-gray-900">
                    {groupKey === 'Unassigned'
                      ? 'Unassigned Area'
                      : `${groupKey}`}
                    <span className="ml-2 text-sm font-normal text-gray-600">
                      ({groupTasks.length})
                    </span>
                  </h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {groupTasks.map((task) => (
                    <div
                      key={task.id}
                      onClick={() => handleViewTask(task)}
                      className="group hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                    >
                      <div className="px-6 py-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            {/* Title and status row */}
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-gray-900">
                                {task.title}
                              </h4>
                              {/* Quick status change dropdown */}
                              {canEdit && (
                                <select
                                  value={formatTaskStatus(task.status)}
                                  onChange={(e) => {
                                    // Convert Title Case from select to snake_case for backend
                                    const statusMap: Record<string, string> = {
                                      'Not Started': 'not_started',
                                      'In Progress': 'in_progress',
                                      Complete: 'complete',
                                      Blocked: 'blocked',
                                    };
                                    handleUpdateTask(task.id, {
                                      status: statusMap[e.target.value] as any,
                                    });
                                  }}
                                  className="text-xs border border-gray-200 rounded-md px-2 py-1 bg-gray-50 hover:bg-white focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all cursor-pointer"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <option value="Not Started">
                                    Not Started
                                  </option>
                                  <option value="In Progress">
                                    In Progress
                                  </option>
                                  <option value="Complete">Complete</option>
                                  <option value="Blocked">Blocked</option>
                                </select>
                              )}
                            </div>

                            {/* Priority and area badges */}
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  task.priority === 'high'
                                    ? 'bg-red-100 text-red-800'
                                    : task.priority === 'medium'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {task.priority
                                  ? task.priority.charAt(0).toUpperCase() +
                                    task.priority.slice(1)
                                  : ''}
                              </span>
                              {task.area_name && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-500 text-white">
                                  {task.area_name}
                                </span>
                              )}
                              {!canEdit && (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    formatTaskStatus(task.status) === 'Complete'
                                      ? 'bg-green-100 text-green-800'
                                      : formatTaskStatus(task.status) ===
                                          'In Progress'
                                        ? 'bg-blue-100 text-blue-800'
                                        : formatTaskStatus(task.status) ===
                                            'Blocked'
                                          ? 'bg-red-100 text-red-800'
                                          : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {formatTaskStatus(task.status)}
                                </span>
                              )}
                            </div>

                            {/* Description */}
                            {task.description && (
                              <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                                {task.description}
                              </p>
                            )}

                            {/* Metadata row */}
                            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                              {task.assigned_to_id && (
                                <div className="flex items-center gap-1">
                                  <UserIcon className="w-3 h-3 text-gray-400" />
                                  <span>
                                    {organizationMembers.find(
                                      (m) => m.user_id === task.assigned_to_id,
                                    )?.user_name ||
                                      organizationMembers.find(
                                        (m) =>
                                          m.user_id === task.assigned_to_id,
                                      )?.user_email}
                                  </span>
                                </div>
                              )}
                              {task.due_date && (
                                <div className="flex items-center gap-1">
                                  <CalendarIcon className="w-3 h-3 text-gray-400" />
                                  <span>
                                    {new Date(task.due_date).toLocaleDateString(
                                      'en-US',
                                      {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                      },
                                    )}
                                  </span>
                                </div>
                              )}
                              {task.context_type &&
                                task.context_object_id &&
                                task.context_type !== 'organization' && (
                                  <div className="flex items-center gap-1">
                                    <Squares2X2Icon className="w-3 h-3 text-gray-400" />
                                    <span>
                                      {task.context_type === 'position' &&
                                        getPositionName(task.context_object_id)}
                                      {task.context_type === 'segment' &&
                                        segments.find(
                                          (s) =>
                                            s.id === task.context_object_id,
                                        )?.name}
                                      {task.context_type === 'proposition' &&
                                        propositions.find(
                                          (p) =>
                                            p.id === task.context_object_id,
                                        )?.name}
                                      {task.context_type === 'group' &&
                                        groups.find(
                                          (g) =>
                                            g.id === task.context_object_id,
                                        )?.name}
                                      {task.context_type === 'company' &&
                                        companies.find(
                                          (c) =>
                                            c.id === task.context_object_id,
                                        )?.name}
                                    </span>
                                  </div>
                                )}
                              {task.okr_id && (
                                <div className="flex items-center gap-1">
                                  <RocketLaunchIcon className="w-3 h-3 text-gray-400" />
                                  <span className="truncate max-w-xs">
                                    {(() => {
                                      const okr = allOKRs.find(
                                        (o) => o.id === task.okr_id,
                                      );
                                      if (!okr) return 'Unknown OKR';
                                      return okr.formatted_reference
                                        ? `${okr.formatted_reference}: ${okr.objective}`
                                        : okr.objective;
                                    })()}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          {canEdit && (
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => handleEditTask(task, e)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (task.id) onTaskDelete(task.id);
                                }}
                                className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );

    return (
      <>
        <div className="h-full">
          {viewMode === 'kanban' ? renderKanbanView() : renderListView()}
        </div>

        {/* Task Detail Panel */}
        {selectedTask && (
          <TaskDetailPanel
            task={selectedTask}
            isOpen={showDetailPanel}
            onClose={() => {
              setShowDetailPanel(false);
              setSelectedTask(null);
              // Clear task ID from URL
              setSearchParams({});
            }}
            onUpdate={(updatedTask) => {
              // Update local state with the new task data
              setTasks((prevTasks) =>
                prevTasks.map((t) =>
                  t.id === updatedTask.id ? updatedTask : t,
                ),
              );
              setSelectedTask(updatedTask);
            }}
            onDelete={async (taskId) => {
              await onTaskDelete(taskId);
              setShowDetailPanel(false);
              setSelectedTask(null);
              // Clear task ID from URL
              setSearchParams({});
            }}
            organizationId={organizationId}
            currentUserId={user?.id || ''}
            canEdit={canEdit}
            isAdmin={isAdmin}
            segments={segments}
            propositions={propositions}
            groups={groups}
            companies={companies}
          />
        )}

        {/* Task Create Modal */}
        {showAddTask && (
          <TaskModal
            task={null}
            organizationId={organizationId}
            onClose={() => {
              setShowAddTask(false);
            }}
            onSave={async (createdTask) => {
              await onTaskCreate(createdTask);
              setShowAddTask(false);
            }}
            segments={segments}
            propositions={propositions}
            groups={groups}
            positions={positions}
            companies={companies}
            isAdmin={false}
            initialValues={activeFilters}
          />
        )}
      </>
    );
  }
};

export default UnifiedGravityPlan;
